import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    Box, Typography, Chip, Alert, Stack, CircularProgress,
    Table, TableHead, TableRow, TableCell, TableBody, Tooltip, Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import PublicIcon from '@mui/icons-material/Public';
import DnsIcon from '@mui/icons-material/Dns';
import api from '../services/api';
import ServerPickerInline from '../pages/tools/ServerPickerInline';

/**
 * Modal de teste de resolução DNS — roda query nos nós configurados
 * + DNS público (8.8.8.8 e 1.1.1.1) e mostra comparativo.
 *
 * Útil pra operador validar que um bloqueio está sendo aplicado, ou
 * descobrir vazamentos onde o nó deveria bloquear mas resolveu.
 */
export default function DnsResolutionTest({ open, onClose, inline = false }) {
    const [domain, setDomain] = useState('');
    const [picker, setPicker] = useState({ node_ids: [], public_ids: [], custom_servers: [] });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const reset = () => {
        setDomain('');
        setResult(null);
        setError(null);
        setLoading(false);
    };

    const handleClose = () => {
        if (loading) return;
        reset();
        onClose && onClose();
    };

    const runTest = async () => {
        const d = domain.trim().toLowerCase();
        if (!d || !d.includes('.')) {
            setError('Domínio inválido');
            return;
        }
        const totalSel = (picker.node_ids?.length || 0) + (picker.public_ids?.length || 0) + (picker.custom_servers?.length || 0);
        if (totalSel === 0) {
            setError('Selecione pelo menos 1 servidor DNS');
            return;
        }
        setError(null);
        setLoading(true);
        setResult(null);
        try {
            const res = await api.post('/dns/node/test', {
                domain: d,
                node_ids: picker.node_ids,
                public_ids: picker.public_ids,
                custom_servers: picker.custom_servers,
            });
            setResult(res.data);
        } catch (e) {
            setError(e.response?.data?.detail || e.message || 'Erro no teste');
        } finally {
            setLoading(false);
        }
    };

    const renderStatus = (item) => {
        const { status, expected_blocked } = item;
        const expectsBlock = item._expectsBlock;

        if (status === 'blocked') {
            const ok = expectsBlock;
            return (
                <Tooltip title={ok ? 'Bloqueio aplicado corretamente' : 'Domínio sem bloqueio configurado mas DNS recusou (pode ser DNS upstream do nó)'}>
                    <Chip
                        size="small"
                        icon={<CancelIcon />}
                        label={item.rcode === 'NXDOMAIN' ? 'NXDOMAIN' : (item.rcode === 'NODATA' ? 'NODATA' : 'Bloqueado')}
                        color={ok ? 'success' : 'default'}
                        variant="outlined"
                    />
                </Tooltip>
            );
        }
        if (status === 'resolved') {
            // Resolveu — bom se NÃO devia bloquear; ruim se devia bloquear (vazamento)
            const isLeak = expectsBlock && item.type === 'node';
            return (
                <Tooltip title={isLeak ? '⚠ Vazamento: nó respondeu mesmo com bloqueio configurado' : 'Resolvido normalmente'}>
                    <Chip
                        size="small"
                        icon={isLeak ? <CancelIcon /> : <CheckCircleIcon />}
                        label={isLeak ? 'VAZOU' : 'Resolvido'}
                        color={isLeak ? 'error' : 'info'}
                        variant={isLeak ? 'filled' : 'outlined'}
                    />
                </Tooltip>
            );
        }
        if (status === 'timeout') {
            return <Chip size="small" icon={<HourglassBottomIcon />} label="Timeout" color="warning" variant="outlined" />;
        }
        return (
            <Tooltip title={item.error || 'Erro desconhecido'}>
                <Chip size="small" icon={<HelpOutlineIcon />} label="Erro" color="error" variant="outlined" />
            </Tooltip>
        );
    };

    const body = (
        <>
                {!inline && (
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Informe um domínio para consultar em todos os nós DNS configurados
                        e comparar com servidores públicos. Útil pra validar bloqueios ou
                        identificar vazamentos.
                    </Typography>
                )}

                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Domínio (ex.: facebook.com)"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                runTest();
                            }
                        }}
                        disabled={loading}
                        autoFocus
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={runTest}
                        disabled={loading || !domain.trim()}
                        sx={{ minWidth: 110 }}
                    >
                        {loading ? <CircularProgress size={20} /> : 'Testar'}
                    </Button>
                </Stack>

                <ServerPickerInline value={picker} onChange={setPicker} disabled={loading} defaultPublic={true} />

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                )}

                {result && (
                    <Box>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography variant="body2">
                                <strong>{result.domain}</strong>
                            </Typography>
                            {result.expected_blocked ? (
                                <Chip
                                    size="small"
                                    color="warning"
                                    label={`Deve estar bloqueado (categorias: ${result.matched_categories.join(', ')})`}
                                />
                            ) : (
                                <Chip size="small" color="default" label="Não está em nenhuma blacklist ativa" />
                            )}
                        </Box>

                        {/* Tabela: nós configurados */}
                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <DnsIcon fontSize="small" /> Nós DNS configurados
                        </Typography>
                        <Table size="small" sx={{ mb: 3 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nó</TableCell>
                                    <TableCell>IP</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Resposta</TableCell>
                                    <TableCell align="right">Tempo</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {result.nodes.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 2 }}>
                                            Nenhum nó DNS selecionado no filtro de Servidores acima.
                                            Marque pelo menos um chip de nó pra ver o teste contra ele.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    result.nodes.map((n) => (
                                        <TableRow key={n.node_id}>
                                            <TableCell>{n.name}</TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{n.server}</TableCell>
                                            <TableCell>{renderStatus({ ...n, _expectsBlock: result.expected_blocked })}</TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                                {n.answers && n.answers.length > 0 ? n.answers.join(', ') : (n.rcode || n.error || '—')}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontSize: 12 }}>
                                                {n.time_ms}ms
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>

                        {/* Tabela: público */}
                        <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                            <PublicIcon fontSize="small" /> DNS público (referência)
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Servidor</TableCell>
                                    <TableCell>IP</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Resposta</TableCell>
                                    <TableCell align="right">Tempo</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {result.public.map((p, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{p.name}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{p.server}</TableCell>
                                        <TableCell>{renderStatus({ ...p, _expectsBlock: false })}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                            {p.answers && p.answers.length > 0 ? p.answers.join(', ') : (p.rcode || p.error || '—')}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontSize: 12 }}>
                                            {p.time_ms}ms
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>

                        {/* Conclusão visual */}
                        {result.expected_blocked && result.nodes.some(n => n.status === 'resolved') && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                <strong>Vazamento detectado:</strong> domínio está na blacklist mas
                                {' '}
                                {result.nodes.filter(n => n.status === 'resolved').length} nó(s)
                                {' '}
                                continuam resolvendo. Sincronize esses nós.
                            </Alert>
                        )}
                        {result.expected_blocked && result.nodes.length > 0 && result.nodes.every(n => n.status === 'blocked') && (
                            <Alert severity="success" sx={{ mt: 2 }}>
                                Bloqueio aplicado em todos os nós.
                            </Alert>
                        )}
                    </Box>
                )}
        </>
    );

    if (inline) return <Box>{body}</Box>;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Teste de resolução DNS</DialogTitle>
            <DialogContent dividers>{body}</DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}
